import {hasAnchorPrice} from '../../offer/business/price'
import {OfferPrice} from '../../offer/types/offer'
import {getDisplayPrice, getTotalPrice} from './price'

export interface StayPrice {
  price: number
  anchor: number | null
  showAnchor: boolean
}

/**
 * The nightly and total price for all rooms in the stay. Which one is shown as
 * the main price is a rendering decision.
 */
export interface StayPrices {
  nightly: StayPrice
  total: StayPrice
  currencyCode: string
}

interface RoomOfferPricesParams {
  numberOfRooms: number
  /** Anchor for one room and one night */
  anchorNightly?: number | null
  /** Anchor for one room and the whole stay */
  anchorTotal?: number | null
}

const toStayPrice = (price: number, anchor: number | null): StayPrice => ({
  price,
  anchor,
  showAnchor: anchor !== null && hasAnchorPrice(price, anchor)
})

const anchorForAllRooms = (
  anchor: number | null | undefined,
  numberOfRooms: number
): number | null =>
  typeof anchor === 'number' && !Number.isNaN(anchor)
    ? anchor * numberOfRooms
    : null

/**
 * Returns the nightly and total price of a SAPI.rooms offer.
 *
 * Taxes are already applied: `nightlyDisplayTotal` follows the user's tax
 * flags, while the total always includes taxes and hotel fees.
 * @param prices - The offer prices from SAPI.rooms
 * @param params - The room count and the per-room anchors
 * @returns The prices for all rooms, or null when there's no price to show
 */
export const getRoomOfferPrices = (
  prices: OfferPrice[] | undefined,
  params: RoomOfferPricesParams
): StayPrices | null => {
  const {numberOfRooms, anchorNightly, anchorTotal} = params
  const displayPrice = getDisplayPrice(prices ?? [])
  const currencyCode = displayPrice?.chargeable.currencyCode
  if (!displayPrice || !currencyCode) return null

  // nightlyDisplayTotal is for one room, the total already covers all rooms.
  const nightlyPrice =
    displayPrice.chargeable.nightlyDisplayTotal * numberOfRooms

  return {
    nightly: toStayPrice(
      nightlyPrice,
      anchorForAllRooms(anchorNightly, numberOfRooms)
    ),
    total: toStayPrice(
      getTotalPrice(displayPrice),
      anchorForAllRooms(anchorTotal, numberOfRooms)
    ),
    currencyCode
  }
}
